import { useEffect, useRef, useState } from 'react';
import api from './api';
const sourceLabel = { manual: 'Manual', before_import: 'Before import', before_legacy_import: 'Before legacy import', before_import_undo: 'Before import undo', before_card_delete: 'Before card deletion' };

export default function DataSnapshots() {
  const [rows, setRows] = useState([]);
  const [label, setLabel] = useState('Manual snapshot');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [created, setCreated] = useState(false);
  const [undoPreview, setUndoPreview] = useState(null);
  const [undoReason, setUndoReason] = useState('');
  const [undoConfirmed, setUndoConfirmed] = useState(false);
  const [undoResult, setUndoResult] = useState(null);
  const undoReviewRef = useRef(null);
  const errorRef = useRef(null);
  useEffect(() => { if (undoPreview) undoReviewRef.current?.focus(); }, [undoPreview]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  useEffect(() => {
    let current = true;
    api.dataSnapshots().then(value => { if (current) setRows(value); })
      .catch(err => { if (current) setError(err.message); })
      .finally(() => { if (current) setBusy(false); });
    return () => { current = false; };
  }, []);
  const reloadRows = async successMessage => {
    setRefreshError('');
    try { setRows(await api.dataSnapshots()); }
    catch (err) { setRefreshError(successMessage ? `${successMessage}, but the list could not refresh. Use Refresh snapshots to reload it.` : `The snapshot list could not refresh. ${err.message}`); }
  };
  const refresh = async () => {
    setBusy(true); setError('');
    try { await reloadRows(); }
    finally { setBusy(false); }
  };
  const create = async event => {
    event.preventDefault();
    if (!label.trim()) { setError('Enter a snapshot label.'); return; }
    setBusy(true); setError(''); setRefreshError(''); setCreated(false);
    try {
      await api.createDataSnapshot(label.trim());
      setCreated(true);
      await reloadRows('Snapshot created');
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const previewUndo = async id => {
    setBusy(true); setError(''); setUndoPreview(null); setUndoResult(null); setUndoReason(''); setUndoConfirmed(false);
    try { setUndoPreview(await api.previewImportUndo(id)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const executeUndo = async event => {
    event.preventDefault();
    if (!undoConfirmed) { setError('Confirm the reversal and identifier release.'); return; }
    setBusy(true); setError(''); setRefreshError('');
    try {
      const result = await api.undoImport(undoPreview.snapshotId, undoPreview.previewToken, undoReason.trim());
      setUndoResult({ ...result, sourceSnapshotId: undoPreview.snapshotId, batchId: undoPreview.batchId });
      setRows(current => current.map(row => row.id === undoPreview.snapshotId ? { ...row, can_undo_import: false } : row));
      setUndoPreview(null); setUndoReason(''); setUndoConfirmed(false);
      await reloadRows('Import undone');
    } catch (err) { setUndoConfirmed(false); setError(err.message); }
    finally { setBusy(false); }
  };
  return <section className="adminSection" aria-label="Data snapshots">
    <div className="sectionHeader">
      <div><h3>Data snapshots</h3><span>Snapshots are saved automatically before imports and import undo. Create an extra snapshot whenever you need one.</span></div>
      <button type="button" className="popupClose" disabled={busy} onClick={refresh}>Refresh snapshots</button>
    </div>
    <p className="adminMuted">Includes cards, portraits, balances, ledger, approvals, and finance history. Passwords and sign-in sessions are excluded. Download snapshots for safekeeping.</p>
    <p className="adminMuted">Review and undo an unused linked import; nothing changes until confirmation. Used or changed accounts and pending payments can block undo. Manual and legacy snapshots are download-only.</p>
    <form className="adminFilters" method="post" action="/api/admin/data-snapshots" onSubmit={create}>
      <label htmlFor="snapshot-label">Snapshot label<input id="snapshot-label" name="label" required maxLength="120" disabled={busy} value={label} onChange={event => setLabel(event.target.value)} /></label>
      <button type="submit" className="popupClose adminPrimary" disabled={busy}>{busy ? 'Please wait…' : 'Create snapshot'}</button>
    </form>
    {error && <p className="adminError" role="alert" tabIndex="-1" ref={errorRef}>{error}</p>}
    {refreshError && <p className="adminError" role="alert">{refreshError}</p>}
    {created && <p className="adminSuccess" role="status">Snapshot created.</p>}
    {undoResult && <p className="adminSuccess" role="status">Import undone. Card IDs and UINs released; financial history retained. Source snapshot: <code>{undoResult.sourceSnapshotId}</code>. Batch: <code>{undoResult.batchId}</code>. Safety snapshot: <code>{undoResult.snapshotId}</code>.</p>}
    {undoPreview && <div className="eventDetail">
      <h4 tabIndex="-1" ref={undoReviewRef}>Review import undo</h4>
      <p>Source snapshot: <code>{undoPreview.snapshotId}</code></p>
      <p>Batch: <code>{undoPreview.batchId}</code> · {undoPreview.cards} cards · Reverse {(undoPreview.valueSatang / 100).toFixed(2)} THB</p>
      <p>{undoPreview.skipped ?? 0} skipped rows remain unchanged.</p>
      <p className="adminMuted">The following card IDs and UINs will be released. Skipped cards and unrelated activity are preserved. A fresh safety snapshot is saved before execution.</p>
      <div className="adminTableWrap"><table className="historyTable"><caption>Identifiers to release</caption><thead><tr><th scope="col">Card ID</th><th scope="col">UIN</th><th scope="col">Owner</th><th scope="col">Value</th></tr></thead><tbody>{undoPreview.mappings.map(row => <tr key={row.cardId}><td>{row.cardId}</td><td>{row.uin}</td><td>{row.owner}</td><td>{(row.balanceSatang / 100).toFixed(2)} THB</td></tr>)}</tbody></table></div>
      <form method="post" action={`/api/admin/data-snapshots/${undoPreview.snapshotId}/import-undo`} onSubmit={executeUndo}>
        <div className="adminFilters"><label htmlFor="import-undo-reason">Import undo reason<input id="import-undo-reason" name="reason" required minLength="5" maxLength="500" disabled={busy} value={undoReason} onChange={event => setUndoReason(event.target.value)} /></label></div>
        <label className="dataConfirmation" htmlFor="import-undo-confirm"><input id="import-undo-confirm" name="confirm" type="checkbox" required disabled={busy} checked={undoConfirmed} onChange={event => setUndoConfirmed(event.target.checked)} /> I confirm reversal of the displayed value and release of these identifiers</label>
        <div className="adminActions"><button type="button" className="popupClose" disabled={busy} onClick={() => setUndoPreview(null)}>Cancel undo</button><button type="submit" className="popupClose adminPrimary" disabled={busy}>Undo import and release identifiers</button></div>
      </form>
    </div>}
    <div className="adminTableWrap"><table className="historyTable">
      <thead><tr><th>Captured</th><th>Label</th><th>Source</th><th>Cards</th><th>Download</th></tr></thead>
      <tbody>{rows.map(row => <tr key={row.id}>
        <td>{new Date(row.captured_at).toLocaleString()}</td><td>{row.label}<small className="historySubtext">Snapshot: <code>{row.id}</code></small></td>
        <td>{sourceLabel[row.kind] || row.kind}{row.operation_reference && <small className="historySubtext">{row.kind === 'before_import' ? 'Batch' : 'Reference'}: <code>{row.operation_reference}</code></small>}</td><td>{row.row_counts?.cards ?? '—'}</td>
        <td><a href={api.dataSnapshotUrl(row.id)}>Download snapshot</a><small className="historySubtext">{(Number(row.compressed_bytes) / 1024 / 1024).toFixed(2)} MB</small>{Boolean(row.can_undo_import) && <button type="button" className="tableAction" disabled={busy} onClick={() => previewUndo(row.id)}>Undo related import<span className="visuallyHidden"> {row.label} {row.id}</span></button>}</td>
      </tr>)}</tbody>
    </table>{!busy && rows.length === 0 && <p className="emptyState">No snapshots yet.</p>}</div>
  </section>;
}
